"use client";

import React from "react";
import {
  type CollaboratorsOnLists,
  type FollowersOnLists,
  type List,
  type GoogleImage,
  type PlaceAffiliate,
  type Place,
  type Spot,
  type SpotImage,
  type Trip,
  type TripDay,
  type TripItem,
  type User,
  type UserSpotFav,
  type UserSpotVisit,
  SaveType,
} from "@prisma/client";
import dayjs from "dayjs";
import Image from "next/image";
import UserIcon from "@/components/user-icon";
import SpotView from "@/components/v3/SpotView";
import { FavoriteIcon } from "@/app/(v3)/icons/FavoriteIcon";
import { WhiteButton } from "@/components/v3/WhiteButton";
import { ArrowBackIcon } from "@/app/(v3)/icons/ArrowBackIcon";
import localizedFormat from "dayjs/plugin/localizedFormat";
import classNames from "classnames";
import BackButton from "@/components/v3/BackButton";
import SaveButton from "@/components/v3/SaveButton";

dayjs.extend(localizedFormat);

export const GuidesViewContent = ({
  list,
  currentUser,
  className,
}: {
  list: List & {
    user: User;
    spots: (Spot & {
      images: SpotImage[];
      place: Place & { googleImages: GoogleImage[]; affiliates: PlaceAffiliate[] };
      list: List & { user: User; spots: Spot[] };
      user: User;
    })[];
  };
  currentUser:
    | (User & {
        lists: (List & { spots: Spot[] })[];
        followingLists: FollowersOnLists[];
        collaboratingLists: CollaboratorsOnLists[];
        favedSpots: UserSpotFav[];
        visitedSpots: UserSpotVisit[];
        trips: (Trip & {
          tripDays: (TripDay & {
            items: (TripItem & {
              spot: Spot | null;
            })[];
          })[];
        })[];
      })
    | null;
  className?: string;
}): JSX.Element => {
  const time = dayjs(list.updatedAt).format("ll");
  return (
    <div className={classNames("bg-variable-collection-neutral-200", className)}>
      <div className="relative h-96">
        <Image
          className="object-cover"
          src={list.photos[0] ?? "/images/image-cover-" + (1 + (list.spots.length % 9)) + ".png"}
          alt=""
          fill
        />
        <div className="absolute inset-x-4 top-14 flex justify-between">
          <BackButton defaultUrl="/discover">
            <WhiteButton onClick={() => {}}>
              <ArrowBackIcon className="h-5 w-5" />
            </WhiteButton>
          </BackButton>

          <div className="absolute right-[0px]">
            <SaveButton type={SaveType.LIST} id={list.id} />
          </div>
        </div>
        <div className="absolute bottom-4 left-4 w-3/4 font-medium text-white">
          <time className="text-sm font-medium" dateTime={time}>
            {time}
          </time>
          <div className="my-2 font-mackinac text-2xl">{list.name}</div>
          <div className="text-xs font-medium text-white">
            <UserIcon user={list.user} showDisplayName={true} size={5} link={false} />
          </div>
        </div>
        <div className="absolute bottom-4 right-4 rounded-lg bg-[#000000bf] px-3 py-2.5 text-white">{`${list.spots.length} spot${list.spots.length > 1 ? "s" : ""}`}</div>
      </div>

      <div className="px-5 pb-6">
        <div className="py-6"> {list.description}</div>

        <div className="py-6 text-2xl font-medium">Spots</div>

        <div className="flex flex-col gap-6 md:grid md:grid-cols-[repeat(auto-fill,minmax(353px,1fr))]">
          {list.spots.map((spot) => (
            <div key={spot.id} id={spot.slug}>
              <SpotView currentUser={currentUser} spot={spot} fullMode />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
